import moment from 'moment';
import { documentSchemas } from 'schemas';
import {
    DistributorEnrichments,
    distributorType,
    docTypes,
    UserEnrichments,
} from 'utils/constants';
import { corporateForms } from 'utils/formData';
import { convertToBoolOrNull, convertToBoolOrUndefined, createDocumentsIRI } from 'utils/helpers';
import { sendDistributorKYC } from './tunnel';

export const getDistributorList = ({
    itemsPerPage,
    direction,
    orderBy,
    distributor_activity_0_3,
    distributor_activity_3_7,
    distributor_activity_7_30,
    distributor_activity_30_plus,
    requestForDistribution,
    conventionPartiallySigned,
    distributorWithoutCommercial,
    distributorSubscriptionInProgress,
    noConventionFullySigned,
    oneOrMoreConventionsFullySigned,
    oneOrMoreSubscriptionsFinished,
    network,
    keyword,
    conventionWithFund,
    networkInvestmentFunds,
    conventionSignedWithFund,
    ...rest
}) => ({
    per_page: itemsPerPage,
    order_direction: direction,
    order_by: orderBy,
    filter_distributor_activity_0_3: distributor_activity_0_3,
    filter_distributor_activity_3_7: distributor_activity_3_7,
    filter_distributor_activity_7_30: distributor_activity_7_30,
    filter_distributor_activity_30_plus: distributor_activity_30_plus,
    filter_requestForDistribution: requestForDistribution,
    filter_conventionPartiallySigned: conventionPartiallySigned,
    filter_distributorSubscriptionInProgress: distributorSubscriptionInProgress,
    filter_noConventionFullySigned: noConventionFullySigned,
    filter_oneOrMoreConventionsFullySigned: oneOrMoreConventionsFullySigned,
    filter_oneOrMoreSubscriptionsFinished: oneOrMoreSubscriptionsFinished,
    filter_conventionSignedWithFund: conventionSignedWithFund,
    filter_network: network,
    filter_distributorCompanyName: keyword,
    filter_conventionWithFund: conventionWithFund,
    filter_networkInvestmentFunds: networkInvestmentFunds,
    filter_distributorWithoutCommercial: distributorWithoutCommercial,
    ...rest,
});

export const getDistributorCompanyEnriched = companyId => ({
    filter_companyId: companyId,
    enrich_with: [
        UserEnrichments.NOTIFICATIONS,
        UserEnrichments.COMMENTS,
        UserEnrichments.DOCUMENTS,
        UserEnrichments.SUBSCRIPTIONS,
        DistributorEnrichments.NETWORK,
        DistributorEnrichments.PROMOTERS,
    ],
});

export const getDistributorBills = () => {
    const today = moment();
    const isFirstQuarter = today.get('month') < 3;

    return {
        year: today.get('year') - (isFirstQuarter ? 1 : 0),
        quarter: isFirstQuarter ? 4 : parseInt(today.get('month') / 3),
    };
};

const getDistributorPayload = obj => ({
    corporateName: obj.corporateName,
    corporateForm: JSON.stringify({
        choice: obj.corporateForm,
        ...(obj.corporateForm === corporateForms.other && { other: obj.otherCorporateForm }),
    }),
    type: obj.type,
    billingEmails: obj.type === distributorType.STANDARD ? true : obj.billingEmails,
    commissionsMenu: obj.type === distributorType.STANDARD ? true : obj.commissionsMenu,
    organization: convertToBoolOrUndefined(obj.organization),
    function: obj.function,
    shareCapital: +obj.shareCapital,
    currentLocation: {
        city: obj.currentAddress.city,
        zipCode: obj.currentAddress.zipCode,
        address: obj.currentAddress.address,
        country: obj.currentAddress.country,
        formattedAddress:
            obj.currentAddress.formattedAddress ||
            `${obj.currentAddress.address}, ${obj.currentAddress.zipCode} ${obj.currentAddress.city}, ${obj.currentAddress.country}`,
    },
    legalRepresentant: convertToBoolOrNull(obj.legalRepresentant, 'yes', 'no'),
    representativeName: obj.representativeName || '',
    representativeRole: obj.representativeRole || '',
    ...sendDistributorKYC(obj).distributorCompany,
    companyLogo: createDocumentsIRI(obj.logo?.id),
    certificateORIAS: createDocumentsIRI(obj.oriasAttestation?.id),
    kbis: createDocumentsIRI(obj.kbis?.id),
    insuranceCertificate: createDocumentsIRI(obj.insuranceCertificate?.id),
    legalRepresentativeIdentityDocument: createDocumentsIRI(obj.legalRepresentantId?.id),
    legalRepresentativeDocument: createDocumentsIRI(obj.legalRepresentantAuthority?.id),
    signatoryIdentityDocument: createDocumentsIRI(obj.signatoryId?.id),
    beneficiariesRegister: createDocumentsIRI(obj.beneficiariesRegister?.id),
    // Handle multiple document arrays
    beneficiariesIdentityDocuments:
        obj.beneficiariesIdentityDocuments?.length > 0
            ? obj.beneficiariesIdentityDocuments
                  .map(doc => createDocumentsIRI(doc.id))
                  .filter(Boolean)
            : undefined,
    legalRepresentativesIdentityDocuments:
        obj.legalRepresentativesIdentityDocuments?.length > 0
            ? obj.legalRepresentativesIdentityDocuments
                  .map(doc => createDocumentsIRI(doc.id))
                  .filter(Boolean)
            : undefined,
    user: {
        title: obj.title,
        firstName: obj.firstName.trim(),
        lastName: obj.lastName.trim().toUpperCase(),
        email: obj.email.trim(),
        phoneNumber: obj.phoneNumber,
        subscriptions: [
            {
                bankingStatement: { ribDocument: createDocumentsIRI(obj.rib?.id) },
                role: `/api/roles/${obj.role}`,
            },
        ],
    },
});

export const createDistributor = (obj, conventions) => ({
    ...getDistributorPayload(obj),
    promoters: obj.promoters.map(p => `/api/promoters/${p.id}`),
    conventions: conventions.length
        ? obj.products
              .filter(p => p.checked)
              .map((p, i) => ({
                  investmentFund: `/api/investment_funds/${p.id}`,
                  document: `/api/documents/${conventions[i].id}`,
              }))
        : undefined,
});

export const updateDistributor = (obj, newConventions, replacedConventions) => {
    const replaced = obj.products.filter(p => p.replaced);
    const added = obj.products.filter(p => !p.convention?.id && p.convention && !p.replaced);
    const unchanged = obj.products.filter(p => !p.replaced && p.checked && p.convention?.id);
    const noChanges = !replaced.length && !added.length;

    return {
        ...getDistributorPayload(obj),
        id: obj.id,
        promoters: obj.promoters.map(p => `/api/promoters/${p.id}`),
        conventions: noChanges
            ? undefined
            : [
                  ...unchanged.map(p => `/api/conventions/${p.conventionId}`),
                  ...replaced.map((p, i) => ({
                      id: p.conventionId,
                      investmentFund: `/api/investment_funds/${p.id}`,
                      document: `/api/documents/${replacedConventions[i].id}`,
                  })),
                  ...added.map((p, i) => ({
                      investmentFund: `/api/investment_funds/${p.id}`,
                      document: `/api/documents/${newConventions[i].id}`,
                  })),
              ],
    };
};

export const updateDistributorCompany = ({ id, emails, emailsKey, ...obj }) => ({
    id,
    [emailsKey]: JSON.stringify(emails),
    ...obj,
});

export const distributorCompanyDocuments = payload => {
    let companyDocumentsPayload = {};

    if (!payload.oriasAttestation?.id && payload.oriasAttestation) {
        companyDocumentsPayload.oriasAttestation = documentSchemas.sendDocuments(
            payload.oriasAttestation,
            docTypes.COMPANY,
            'oriasAttestation'
        );
    }
    if (!payload.kbis?.id && payload.kbis) {
        companyDocumentsPayload.kbis = documentSchemas.sendDocuments(
            payload.kbis,
            docTypes.COMPANY,
            'kbis'
        );
    }
    if (!payload.insuranceCertificate?.id && payload.insuranceCertificate) {
        companyDocumentsPayload.insuranceCertificate = documentSchemas.sendDocuments(
            payload.insuranceCertificate,
            docTypes.COMPANY,
            'insuranceCertificate'
        );
    }
    if (!payload.signatoryId?.id && payload.signatoryId) {
        companyDocumentsPayload.signatoryId = documentSchemas.sendDocuments(
            payload.signatoryId,
            docTypes.COMPANY,
            'signatoryId'
        );
    }
    if (!payload.rib?.id && payload.rib) {
        companyDocumentsPayload.rib = documentSchemas.sendDocuments(
            payload.rib,
            docTypes.RIB,
            'rib'
        );
    }
    if (payload.legalRepresentant === 'no') {
        if (!payload.legalRepresentantAuthority?.id && payload.legalRepresentantAuthority) {
            companyDocumentsPayload.legalRepresentantAuthority = documentSchemas.sendDocuments(
                payload.legalRepresentantAuthority,
                docTypes.COMPANY,
                'legalRepresentantAuthority'
            );
        }
        if (!payload.legalRepresentantId?.id && payload.legalRepresentantId) {
            companyDocumentsPayload.legalRepresentantId = documentSchemas.sendDocuments(
                payload.legalRepresentantId,
                docTypes.COMPANY,
                'legalRepresentantId'
            );
        }
    }

    if (!payload.beneficiariesRegister?.id && payload.beneficiariesRegister) {
        companyDocumentsPayload.beneficiariesRegister = documentSchemas.sendDocuments(
            payload.beneficiariesRegister,
            docTypes.COMPANY,
            'beneficiariesRegister'
        );
    }

    // Handle multiple document arrays - only upload new files
    if (payload.beneficiariesIdentityDocuments?.length > 0) {
        const newFiles = payload.beneficiariesIdentityDocuments.filter(doc => doc instanceof File);
        if (newFiles.length > 0) {
            companyDocumentsPayload.beneficiariesIdentityDocuments = documentSchemas.sendDocuments(
                newFiles,
                docTypes.COMPANY,
                'beneficiariesIdentityDocuments'
            );
        }
    }
    if (payload.legalRepresentativesIdentityDocuments?.length > 0) {
        const newFiles = payload.legalRepresentativesIdentityDocuments.filter(
            doc => doc instanceof File
        );
        if (newFiles.length > 0) {
            companyDocumentsPayload.legalRepresentativesIdentityDocuments =
                documentSchemas.sendDocuments(
                    newFiles,
                    docTypes.COMPANY,
                    'legalRepresentativesIdentityDocuments'
                );
        }
    }

    return companyDocumentsPayload;
};
